import { useState } from 'react';
import { AccommodationItemCard } from '../../components/AccommodationItemCard';
import { Button } from '../../components/Button';
import { ContentParser } from '../../components/ContentParser';
import { ExpandableText } from '../../components/ExpandableText';
import { NotificationBox } from '../../components/NotificationBox';
import { NotificationActions } from '../../components/NotificationBox/NotificationBox.styles';
import { SectionHeader } from '../../components/SectionHeader';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { StickyActionBar } from '../../components/StickyActionBar';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import {
  ActionBarArea,
  AlternativesList,
  AlternativesSection,
  DescriptionSection,
  HeaderGroup,
  ItemsItem,
  ItemsSection,
  MetaLabel,
  NotificationSection,
  RecoverySection,
  RecoveryToast,
  ScrollArea,
  ShowMoreRow,
  Spacer,
  Title,
  TriggerRow,
  TriggerSection,
  UrgencyBadge,
  Wrapper,
} from './AccommodationContentPanel.styles';
import type {
  AccommodationContentPanelProps,
  AccommodationUnavailableRecovery,
} from './AccommodationContentPanel.types';

const META_SEPARATOR = ' · ';
const DEFAULT_ITEMS_SHOWN = 3;

function UnavailableRecovery({
  message,
  dismissLabel,
  nextStay,
  alternatives,
}: AccommodationUnavailableRecovery) {
  const [isMessageDismissed, setIsMessageDismissed] = useState(false);
  const alternativeItems = alternatives?.items ?? [];

  return (
    <RecoverySection>
      {!isMessageDismissed && (
        <RecoveryToast
          tone="warning"
          variant="fill"
          onDismiss={() => setIsMessageDismissed(true)}
          dismissLabel={dismissLabel}
        >
          {message}
        </RecoveryToast>
      )}
      {nextStay && (
        <NotificationBox
          title={nextStay.title}
          subtitle={nextStay.subtitle}
          cta={
            <Button variant="primary" size="large" fullWidth onClick={nextStay.onCtaClick}>
              {nextStay.ctaLabel}
            </Button>
          }
        />
      )}
      {alternatives && alternativeItems.length > 0 && (
        <AlternativesSection>
          <SectionHeader title={alternatives.title} subtitle={alternatives.subtitle} />
          <AlternativesList>
            {alternativeItems.map((item) => (
              <li key={item.id}>
                <AccommodationItemCard
                  name={item.name}
                  image={item.imageUrl ? { src: item.imageUrl, alt: item.imageAlt } : undefined}
                  price={item.price}
                  priceLabel={alternatives.fromPriceLabel}
                  detailsLabel={alternatives.onViewItemDetails ? alternatives.detailsLabel : ''}
                  onSelect={
                    alternatives.onViewItemDetails
                      ? () => alternatives.onViewItemDetails?.(item.id)
                      : undefined
                  }
                  onCardClick={() => alternatives.onSelectItem(item.id)}
                />
              </li>
            ))}
          </AlternativesList>
        </AlternativesSection>
      )}
    </RecoverySection>
  );
}

export function AccommodationContentPanel({
  title,
  urgencyLabel,
  items = [],
  showItemImages = true,
  onSelectItem,
  selectItemLabel = 'More details',
  selectedItemId,
  onItemSelect,
  initialItemsShown = DEFAULT_ITEMS_SHOWN,
  showMoreItemsLabel = 'Show more',
  showLessItemsLabel = 'Show less',
  editSearchLabel,
  onEditSearch,
  metaLabels,
  description,
  descriptionShowMoreLabel,
  descriptionShowLessLabel,
  modalTriggerLabel,
  onModalTriggerClick,
  onReserve,
  reserveLabel = 'Reserve',
  notice,
  unavailable,
  unavailableRecovery,
  isLoading = false,
}: AccommodationContentPanelProps) {
  const [allItemsShown, setAllItemsShown] = useState(false);

  if (isLoading) {
    return (
      <Wrapper aria-busy="true">
        <ScrollArea>
          <HeaderGroup>
            <SkeletonBlock variant="heading" />
          </HeaderGroup>
          <ItemsSection>
            <SkeletonBlock height="96px" />
            <SkeletonBlock height="96px" />
            <SkeletonBlock height="96px" />
          </ItemsSection>
          <DescriptionSection>
            <SkeletonBlock variant="text" />
            <SkeletonBlock variant="text" />
            <SkeletonBlock variant="text" width="70%" />
          </DescriptionSection>
          <Spacer />
        </ScrollArea>
        <ActionBarArea>
          <StickyActionBar>
            <SkeletonBlock variant="button" />
          </StickyActionBar>
        </ActionBarArea>
      </Wrapper>
    );
  }

  const visibleItems = allItemsShown ? items : items.slice(0, initialItemsShown);
  const hasHiddenItems = items.length > initialItemsShown;
  const meta = metaLabels?.filter(Boolean) ?? [];

  return (
    <Wrapper>
      <ScrollArea>
        <HeaderGroup>
          {urgencyLabel && <UrgencyBadge>{urgencyLabel}</UrgencyBadge>}
          <Title>{title}</Title>
        </HeaderGroup>
        {notice && (
          <NotificationSection>
            <NotificationBox title={notice.title} subtitle={notice.subtitle} />
          </NotificationSection>
        )}
        {unavailableRecovery && <UnavailableRecovery {...unavailableRecovery} />}
        {visibleItems.length > 0 && (
          <ItemsSection>
            {visibleItems.map((item) => (
              <ItemsItem key={item.id}>
                <AccommodationItemCard
                  name={item.name}
                  image={item.imageUrl ? { src: item.imageUrl, alt: item.imageAlt } : undefined}
                  showImage={showItemImages}
                  price={item.price}
                  discount={item.discount}
                  secondaryPriceLabel={item.secondaryPriceLabel}
                  priceNote={item.priceNote}
                  promotionName={item.promotionName}
                  detailsLabel={selectItemLabel}
                  onSelect={onSelectItem ? () => onSelectItem(item.id) : undefined}
                  selected={selectedItemId === item.id}
                  onCardClick={onItemSelect ? () => onItemSelect(item.id) : undefined}
                  unavailable={item.unavailable}
                  unavailableLabel={item.unavailableLabel}
                  editSearchLabel={editSearchLabel}
                  onEditSearch={onEditSearch}
                />
              </ItemsItem>
            ))}
          </ItemsSection>
        )}
        {hasHiddenItems && (
          <ShowMoreRow>
            <Button
              variant="neutral"
              aria-expanded={allItemsShown}
              onClick={() => setAllItemsShown(!allItemsShown)}
            >
              {allItemsShown ? showLessItemsLabel : showMoreItemsLabel}
            </Button>
          </ShowMoreRow>
        )}
        {meta.length > 0 && <MetaLabel>{meta.join(META_SEPARATOR)}</MetaLabel>}
        {!!description?.length && (
          <DescriptionSection>
            <ExpandableText
              text={description}
              showMoreLabel={descriptionShowMoreLabel}
              showLessLabel={descriptionShowLessLabel}
            />
          </DescriptionSection>
        )}
        {modalTriggerLabel && (
          <TriggerSection>
            <TriggerRow type="button" onClick={onModalTriggerClick}>
              {modalTriggerLabel}
              <ChevronRightIcon />
            </TriggerRow>
          </TriggerSection>
        )}
        {unavailable && (
          <NotificationSection>
            <NotificationBox
              title={unavailable.title}
              subtitle={
                unavailable.subtitle ? <ContentParser text={unavailable.subtitle} /> : undefined
              }
              cta={
                unavailable.ctaLabel || unavailable.secondaryCtaLabel ? (
                  <NotificationActions
                    $stacked={!!unavailable.ctaLabel && !!unavailable.secondaryCtaLabel}
                  >
                    {unavailable.ctaLabel && (
                      <Button
                        variant="primary"
                        size={unavailable.secondaryCtaLabel ? 'large' : undefined}
                        fullWidth={!!unavailable.secondaryCtaLabel}
                        onClick={unavailable.onCtaClick}
                      >
                        {unavailable.ctaLabel}
                      </Button>
                    )}
                    {unavailable.secondaryCtaLabel && (
                      <Button
                        variant="secondary"
                        size="large"
                        fullWidth={!!unavailable.ctaLabel}
                        onClick={unavailable.onSecondaryCtaClick}
                      >
                        {unavailable.secondaryCtaLabel}
                      </Button>
                    )}
                  </NotificationActions>
                ) : undefined
              }
            />
          </NotificationSection>
        )}
        <Spacer />
      </ScrollArea>
      {!unavailable && onReserve && (
        <ActionBarArea>
          <StickyActionBar>
            <Button
              variant="primary"
              fullWidth
              onClick={onReserve}
              disabled={!!unavailableRecovery}
            >
              {reserveLabel}
            </Button>
          </StickyActionBar>
        </ActionBarArea>
      )}
    </Wrapper>
  );
}
